/*
 * Copyright (C) Fraunhofer IESE 2021-2025 - Mher Ter-Tovmasyan
 *
 * SPDX-License-Identifier: AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import { yupResolver } from '@hookform/resolvers/yup';
import {
  getFormattedDate,
  getFormattedTime,
  getIcon,
  isEmptyOrNull,
  SLRFormInfo,
  SLRLoadingButton,
  SLRPageHead,
  SLRSpinner,
  SLRTextEditor,
  useGetCurrentUserProfile
} from '@SLR/shared-library';
import { DraftStatus, OrganizationType } from '@SLR/user-service-full-sdk';
import { default as MarkdownPreview } from '@uiw/react-markdown-preview';
import { ADMIN_ONBOARDING_REQUESTS_PATH } from 'configs';
import { useAcceptOrganizationDraft } from 'hooks/organization-draft/useAcceptOrganizationDraft';
import { useDeleteOrganizationDraft } from 'hooks/organization-draft/useDeleteOrganizationDraft';
import { useGetOrganizationDraft } from 'hooks/organization-draft/useGetOrganizationDraft';
import { useRejectOrganizationDraft } from 'hooks/organization-draft/useRejectOrganizationDraft';
import { useUpdateOrganizationDraft } from 'hooks/organization-draft/useUpdateOrganizationDraft';
import { FC, useCallback, useMemo, useState } from 'react';
import { Col, Container, Form, Row } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { DraftForm } from '../form/DraftFrom';
import {
  COMMENT_CHAR_LENGTH,
  OrganizationDraftInput,
  OrganizationDraftSchema
} from '../schema/organizationDraft';
import { getOnboardingType } from '../util/draft';

import './RequestPage.scss';

export const RequestPage: FC = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'requestsPage.detail'
  });
  const { draftId } = useParams();
  const { data: user } = useGetCurrentUserProfile();

  const { data: draft } = useGetOrganizationDraft(draftId);
  const { mutate: accept, isPending: isAcceptLoading } =
    useAcceptOrganizationDraft();
  const { mutate: reject, isPending: isRejectLoading } =
    useRejectOrganizationDraft();
  const { mutateAsync: update, isPending: isUpdateLoading } =
    useUpdateOrganizationDraft();
  const { mutate: _delete, isPending: isDeleteLoading } =
    useDeleteOrganizationDraft();

  const navigate = useNavigate();

  const requestType = getOnboardingType(draft?.type, draft?.geoAreaId);

  const form = useForm<OrganizationDraftInput>({
    values: {
      ...draft,
      contactEmailAddress: draft?.contactEmailAddress ?? null,
      contactPerson: draft?.contactPerson ?? null,
      memberEmailAddresses: draft?.memberEmailAddresses ?? null,
      name: draft?.name ?? null,
      type: draft?.type ?? null,
      emails: draft?.memberEmailAddresses?.map((email) => ({ email })) ?? null,
      description: draft?.description ?? null,
      newComment: ''
    },
    resolver: yupResolver(OrganizationDraftSchema(requestType))
  });

  const [note, setNote] = useState<string | undefined>(draft?.note);
  const noteLength = useMemo(() => note?.length ?? 0, [note]);

  const isHandled =
    draft?.status === DraftStatus.Created ||
    draft?.status === DraftStatus.Rejected;

  const handleSaveChanges = useCallback(async () => {
    const draft = form.getValues();
    if (isEmptyOrNull(draftId)) return;

    const memberEmailAddresses = (
      draft.emails?.map(({ email }) => email) ?? []
    ).filter((email) => !isEmptyOrNull(email));
    memberEmailAddresses.push(draft.contactEmailAddress ?? '');

    const comment = isEmptyOrNull(draft.newComment)
      ? draft.comment
      : '<div id="comment"><strong><u>' +
        user?.firstName +
        ' ' +
        user?.lastName +
        ' am ' +
        getFormattedDate(new Date()) +
        ', ' +
        getFormattedTime(new Date()) +
        '</u>:</strong>\n' +
        draft.newComment +
        '\n</div>' +
        (isEmptyOrNull(draft.comment)
          ? ''
          : '<br><span>' + draft.comment + '</span><br>');

    await update({
      organizationDraftId: draftId,
      changeOrganizationDraftRequestDto: {
        additionalInformation: draft.additionalInformation ?? undefined,
        comment: comment ?? undefined,
        contactEmailAddress: draft.contactEmailAddress ?? '',
        contactPerson: draft.contactPerson ?? '',
        geoAreaId: draft.geoAreaId ?? undefined,
        geoAreaIdOrAdditionalInformationSpecified:
          !isEmptyOrNull(draft.geoAreaId) ||
          !isEmptyOrNull(draft.additionalInformation),
        memberEmailAddresses: Array.from(new Set(memberEmailAddresses)),
        name: draft.name ?? '',
        notUserOrganization: true,
        solutionInformation: draft.solutionInformation ?? undefined,
        solutionInformationSpecified: !isEmptyOrNull(draft.solutionInformation),
        type: draft.type ?? OrganizationType.Customer,
        website: draft.website ?? undefined,
        description: draft.description ?? ''
      }
    });
  }, [draftId, form, update, user?.firstName, user?.lastName]);

  const handleAccept = useCallback(async () => {
    if (isEmptyOrNull(draftId)) return;
    await handleSaveChanges();

    accept(
      {
        organizationDraftId: draftId,
        body: isEmptyOrNull(note) ? undefined : note
      },
      { onSuccess: () => navigate(ADMIN_ONBOARDING_REQUESTS_PATH) }
    );
  }, [draftId, handleSaveChanges, accept, note, navigate]);

  const handleReject = useCallback(async () => {
    if (isEmptyOrNull(draftId) || isEmptyOrNull(note)) return;
    await handleSaveChanges();

    reject(
      { organizationDraftId: draftId, body: note },
      { onSuccess: () => navigate(ADMIN_ONBOARDING_REQUESTS_PATH) }
    );
  }, [draftId, note, handleSaveChanges, reject, navigate]);

  const handleDelete = useCallback(() => {
    if (isEmptyOrNull(draftId)) return;

    _delete(
      { organizationDraftId: draftId },
      { onSuccess: () => navigate(ADMIN_ONBOARDING_REQUESTS_PATH) }
    );
  }, [draftId, _delete, navigate]);

  const isLoading = isAcceptLoading || isRejectLoading || isUpdateLoading;

  if (draft) {
    return (
      <Container className="request-page pb-5">
        <SLRPageHead
          className="py-5"
          labels={{
            title: t('title', { name: draft.name }),
            description: t('description')
          }}
          backIcon={getIcon('fal', 'chevron-left')}
          onBackButtonClick={() => navigate(ADMIN_ONBOARDING_REQUESTS_PATH)}
        />
        <FormProvider {...form}>
          <Row className="gap-4 gap-xl-0">
            <Col xs="12" xl="6" className="person">
              <Row className="gap-4">
                <Col xs="12">
                  <h2>{t(`requestType.${requestType}`)}</h2>
                </Col>
                <DraftForm onboardingType={requestType} />
              </Row>
            </Col>
            <Col xs="12" xl="6" className="d-flex flex-column gap-4 handle">
              <Row>
                <Col>
                  <h2>{t('handle.title')}</h2>
                </Col>
              </Row>

              <Row className="gap-2">
                <Col xs="12">
                  <Form.Label htmlFor="note">{t('handle.note')}</Form.Label>
                  <SLRFormInfo text={t('handle.info')} />

                  <Form.Control
                    id="note"
                    as="textarea"
                    className="mt-2"
                    rows={12}
                    disabled={isHandled}
                    onChange={(ev) => setNote(ev.target.value)}
                    value={note}
                  />
                  <SLRFormInfo
                    isInvalid={noteLength > COMMENT_CHAR_LENGTH}
                    counter={{
                      count: noteLength,
                      maxCount: COMMENT_CHAR_LENGTH
                    }}
                  />
                </Col>
              </Row>
              <Row className="justify-content-end">
                <Col xs="auto">
                  <SLRLoadingButton
                    isLoading={isRejectLoading}
                    disabled={isHandled || isEmptyOrNull(note) || isLoading}
                    variant="danger"
                    onClick={handleReject}
                  >
                    {t('handle.reject')}
                  </SLRLoadingButton>
                </Col>
                <Col xs="auto">
                  <SLRLoadingButton
                    isLoading={isAcceptLoading}
                    variant="success"
                    disabled={isHandled || isLoading}
                    onClick={handleAccept}
                  >
                    {t('handle.accept')}
                  </SLRLoadingButton>
                </Col>
                {isHandled ? (
                  <Col xs="12" className="text-end mt-3">
                    {t('handle.handled', {
                      status: t(
                        `handle.${draft.status === DraftStatus.Created ? 'accepted' : 'rejected'}`
                      )
                    })}
                  </Col>
                ) : (
                  <Col xs="8" className="text-end mt-3">
                    <SLRFormInfo text={t('handle.save')} />
                  </Col>
                )}
                {!isHandled && (
                  <Col xs="12" className="text-end mt-3">
                    <SLRLoadingButton
                      isLoading={isDeleteLoading}
                      variant="outline-danger"
                      disabled={isHandled || isDeleteLoading || isLoading}
                      onClick={handleDelete}
                    >
                      {t('handle.delete')}
                    </SLRLoadingButton>
                  </Col>
                )}
              </Row>
            </Col>
          </Row>
          <Row className="justify-content-end pt-4 gap-4">
            <Col xs="12">
              <Form.Label htmlFor="newComment">{t('comment')}</Form.Label>
              <Row className="pb-4">
                <Col xs="auto">{t('history')}</Col>
                <Col>
                  <MarkdownPreview
                    className="markdown-style comment"
                    source={form.getValues('comment') ?? t('noComments')}
                    wrapperElement={{ 'data-color-mode': 'light' }}
                  />
                </Col>
              </Row>

              <SLRTextEditor
                id="newComment"
                control={form.control}
                name="newComment"
                maxLength={COMMENT_CHAR_LENGTH}
                isInvalid={!!form.formState.errors?.newComment}
                errorMessage={form.formState.errors?.newComment?.message}
                rows={12}
              />
            </Col>
            <Col xs="auto">
              <SLRLoadingButton
                width="16rem"
                isLoading={isLoading}
                disabled={isHandled || isLoading}
                onClick={handleSaveChanges}
              >
                {t('save')}
              </SLRLoadingButton>
            </Col>
          </Row>
        </FormProvider>
      </Container>
    );
  }

  return <SLRSpinner />;
};
